/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { FlexRow, FlexCol, Box, Text } from 'sarsaparilla';
import RenderIf from '../utils/JSXUtils';
import { formatPrice } from '../utils';

const Line = {
    description: PropTypes.string,
    quantity: PropTypes.string,
    title: PropTypes.string,
    value: PropTypes.number,
};

function CostsTable({
    attendeeInfo,
    nonResFee,
    lines,
    total,
    ariaLabel,
    showNoLinesText = false,
}) {
    const renderLine = (line, attendeeInfoLine, nonResFeeLine, ariaLabelLine) => {
        const { title, description, value } = line;
        if (!value) return null;

        const cost = formatPrice(value);
        const totalCost = title === 'Total Cost';
        const subTotal = title === 'Subtotal';

        let className = totalCost ? 'parkpass-costs-table-total-cost' : '';
        if (subTotal) className = 'parkpass-costs-table-subtotal';
        let aria = `${title}: ${cost}`;
        if (ariaLabelLine) {
            aria = `${ariaLabelLine} ${aria}`;
        }
        return (
            <FlexRow
                key={title}
                className={`parkpass-costs-table-item-wrap ${className}`}
            >
                <FlexCol xs={6} md={8} xl={6}>
                    <Text size="sm">{title}</Text>
                    {RenderIf(description)(
                        <Text size="sm" tag="span">
                            {description}
                        </Text>
                    )}
                    {attendeeInfoLine && <Text size="sm">{attendeeInfoLine}</Text>}
                    {nonResFeeLine && <Text size="sm">{nonResFeeLine}</Text>}
                </FlexCol>
                <FlexCol
                    aria-label={aria}
                    role="complementary"
                    tabIndex={title === 'Total Cost' || title === 'Subtotal' ? 0 : -1}
                    xs={6}
                    md={4}
                    xl={6}
                    className="parkpass-costs-table-cost"
                >
                    <Text align="right" size="sm">
                        {cost}
                    </Text>
                </FlexCol>
            </FlexRow>
        );
    };

    return (
        <Box>
            {lines.map((line, index) =>
                renderLine(line, index ? null : attendeeInfo, index ? null : nonResFee)
            )}
            {showNoLinesText && (
                <Text>
                    No passes have been selected. Please choose how many passes you will
                    need for this trip.
                </Text>
            )}
            {renderLine(total, null, null, ariaLabel)}
        </Box>
    );
}

CostsTable.propTypes = {
    attendeeInfo: PropTypes.node,
    lines: PropTypes.arrayOf(PropTypes.shape(Line)).isRequired,
    total: PropTypes.shape(Line).isRequired,
    ariaLabel: PropTypes.string,
    showNoLinesText: PropTypes.bool,
    nonResFee: PropTypes.node,
};

export default CostsTable;
